import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from "../../layout/LayoutHeader";
import { postDataAPI } from "../../utils/fetchData";
import axios from 'axios';
import { BASE_URL } from "../../utils/axios";
import usageTracker from "../../utils/usageTracker";
import Modal from "../../components/Modal";
import { useModal } from "../../hooks/useModal";

const BulkImport = () => {
    const [step, setStep] = useState(0);
    const token = localStorage.getItem('dizeeToken');
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();
    const fileInputRef = useRef(null);
    const [uploadedData, setUploadedData] = useState([]);
    const [curEditData, setEditData] = useState(null);
    const [curEditKey, setKey] = useState(-1);
    const [isProcessing, setProcessing] = useState(false);

    const handleCSV = () => {
        setStep(1);
    };

    const handleUpload = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Track the uploaded file (merch could be categorized as label data for analytics)
        try {
            const { trackUploadedFile } = require('../../utils/uploadedFilesTracker');
            trackUploadedFile(file, 'label', {
                source: 'merch-bulk-import',
                status: 'processing',
                subType: 'merch'
            });
        } catch (error) {
            console.error('Error tracking file:', error);
        }
        
        const formData = new FormData();
        formData.append('file', file);
        setProcessing(true);
        try {
            let res = await postDataAPI('aws/csvUploadMerch', formData, token, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 120000, // 2 minutes for large CSV processing
            });
            console.log(`upload data::`, res.data);
            if (res.data.status === 200) {
                setUploadedData(res.data.data);
                setStep(2);
            }
            setProcessing(false);
        } catch (err) {
            console.error('Error processing CSV:', err);
            setProcessing(false);
            showModal('Processing Error', 'Failed to process CSV file. Please try again.', 'error');
            }
    };

    const saveEdit = () => {
        setStep(2);
        let tempData = uploadedData;
        tempData[curEditKey] = curEditData;
        setUploadedData([...tempData]);
        setKey(-1);
    };

    const handleSave = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            
            console.log('🔍 MERCH SAVE DEBUG - Data being sent to backend:', uploadedData);
            console.log('🔍 MERCH SAVE DEBUG - Number of items:', uploadedData.length);
            console.log('🔍 MERCH SAVE DEBUG - Sample item structure:', uploadedData[0]);
            
            const response = await axios.post(
                `${BASE_URL}/user/saveMerch`,
                { data: uploadedData },
                { headers: { Authorization: token } }
            );
            
            console.log('🔍 MERCH SAVE DEBUG - Backend response:', response.data);
            
            if (response.data.status === 200) {
                // Update file status to processed
                try {
                    const { trackUploadedFile } = require('../../utils/uploadedFilesTracker');
                    const fileInput = document.querySelector('input[type="file"]');
                    if (fileInput && fileInput.files && fileInput.files[0]) {
                        const file = fileInput.files[0];
                        trackUploadedFile(file, 'label', {
                            source: 'merch-bulk-import',
                            status: 'processed',
                            processedCount: uploadedData.length,
                            subType: 'merch'
                        });
                    }
                } catch (error) {
                    console.error('Error updating file status:', error);
                }
                
                showModal('Success', 'Merch saved successfully!', 'success');
                
                // Track merch usage for bulk import
                try {
                    const actionType = uploadedData.length > 10 ? 'bulk_import_operation' : 'merch_item_addition';
                    await usageTracker.track(actionType, { 
                        merch_count: uploadedData.length,
                        source: 'bulk_import' 
                    });
                } catch (trackingError) {
                    console.error('Usage tracking failed:', trackingError);
                }
                
                setTimeout(() => {
                    navigate('/merch/view-all');
                }, 1500);
            } else {
                console.log('Save response not successful:', response.data);
                showModal('Save Error', 'Error saving merch. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error saving merch:', error);
            showModal('Save Error', 'Error saving merch. Please try again.', 'error');
        }
    };

    const formatOptions = [
        { name: 'CSV', description: 'Upload a CSV file with merch data' },
        { name: 'Excel', description: 'Excel format support coming soon', disabled: true },
        { name: 'JSON', description: 'JSON format support coming soon', disabled: true }
    ];

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Bulk Import</span>
                        <div className="flex items-center gap-4">
                            {/* Navigation Buttons - Only show when not in review or edit mode */}
                            {step !== 2 && !(step === 3 && curEditData) && (
                                <>
                                    <span 
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                        onClick={() => navigate('/merch/view-all')}
                                    >
                                        View
                                    </span>
                                    
                                    <span 
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                        onClick={() => navigate('/merch/manual-add')}
                                    >
                                        Manual Add
                                    </span>
                                </>
                            )}
                            
                        <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => {
                            if (step === 3) {
                                // If in edit mode, go back to review stage
                                setStep(2);
                                setKey(-1);
                            } else {
                                // Go back to previous page
                                navigate(-1);
                            }
                        }}>Go back</span>
                        </div>
                    </div>
                    
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {step === 0 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Choose your import format</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Select the format of your merch data file</p>
                                </div>

                                <div className="space-y-4">
                                    {formatOptions.map((format, index) => (
                                        <div 
                                            key={index}
                                            className={`bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 transition-all ${
                                                format.disabled 
                                                    ? 'opacity-50 cursor-not-allowed' 
                                                    : 'hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] cursor-pointer'
                                            }`}
                                            onClick={format.disabled ? null : (format.name === 'CSV' ? handleCSV : null)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-white text-[12px] font-semibold mb-1">
                                                        {format.name}
                                                    </h3>
                                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                        {format.description}
                                                    </p>
                                                </div>
                                                {!format.disabled && (
                                                    <div className="w-3 h-3 rounded-full border-2 border-[#9932CC]" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 1 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Upload your CSV file</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Select your merch data CSV file to import</p>
                                </div>

                                <div className="space-y-4">
                                    <div 
                                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-6 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all cursor-pointer text-center"
                                        onClick={handleUpload}
                                    >
                                        <div className="flex flex-col items-center space-y-3">
                                            <div className="w-12 h-12 bg-[rgba(153,50,204,0.2)] rounded-lg flex items-center justify-center">
                                                <svg className="w-6 h-6 text-[#9932CC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                            </div>
                                            <div>
                                                <h3 className="text-white text-[12px] font-semibold mb-1">
                                                    {isProcessing ? 'Processing...' : 'Upload CSV File'}
                                                </h3>
                                                <p className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                    {isProcessing ? 'Please wait while we process your file' : 'Click to select your CSV file'}
                                                </p>
                                            </div>
                                            {isProcessing && (
                                                <div className="w-6 h-6 border-2 border-[#9932CC] border-t-transparent rounded-full animate-spin" />
                                            )}
                                        </div>
                                    </div>

                                    <input 
                                        type="file"
                                        ref={fileInputRef} 
                                        onChange={handleFileChange} 
                                        accept=".csv" 
                                        style={{ display: 'none' }} 
                                    />
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Review imported data</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Verify your merch data before saving</p>
                                </div>

                                <div className="space-y-4 mb-6">
                                    {uploadedData.map((item, index) => (
                                        <div 
                                            key={index}
                                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h3 className="text-white text-[12px] font-semibold mb-1">
                                                        {item.product_name || 'Unknown Product'}
                                                    </h3>
                                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                                                        {item.vendor_brand || 'Unknown Brand'}
                                                    </p>
                                                    <div className="flex items-center space-x-2">
                                                        {item.product_type && (
                                                            <span className="bg-[rgba(153,50,204,0.2)] text-[#9932CC] text-[10px] px-2 py-1 rounded">
                                                                {item.product_type}
                                                            </span>
                                                        )}
                                                        {item.price && (
                                                            <span className="text-[rgba(255,255,255,0.7)] text-[10px]">
                                                                ${item.price}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => { setEditData(item); setStep(3); setKey(index) }}
                                                    className="text-[#9932CC] hover:text-white text-[12px] font-semibold"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all">
                                    <button
                                        onClick={handleSave}
                                        className="w-full bg-[#9932CC] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(153,50,204,0.9)] transition-all"
                                    >
                                        Save All Merch Items
                                    </button>
                                </div>
                        </div>
                    )}
                    
                        {step === 3 && curEditData && (
                            <div className="flex flex-col">
                                <div className="mb-6">
                                    <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Edit merch item</span>
                                    <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">Modify the details for this item</p>
                                </div>

                                <div className="space-y-4 mb-6">
                                    {Object.keys(curEditData).map((key) => (
                                        <div 
                                            key={key}
                                            className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all"
                                        >
                                            <label className="block text-white text-[12px] font-semibold mb-2 capitalize">
                                                {key.replace(/_/g, ' ')}
                                            </label>
                                            <input
                                                type="text"
                                                value={curEditData[key] || ''}
                                                onChange={(e) => setEditData({
                                                    ...curEditData,
                                                    [key]: e.target.value
                                                })}
                                                className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#9932CC] hover:bg-[rgba(153,50,204,0.05)] transition-all">
                                    <button
                                        onClick={saveEdit}
                                        className="w-full bg-[#9932CC] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(153,50,204,0.9)] transition-all"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
        </div>
        
        <Modal
            isOpen={modalConfig.isOpen}
            onClose={closeModal}
            onConfirm={modalConfig.onConfirm}
            title={modalConfig.title}
            message={modalConfig.message}
            type={modalConfig.type}
            showCancel={modalConfig.showCancel}
            confirmText={modalConfig.confirmText}
            cancelText={modalConfig.cancelText}
        />
        </LayoutHeader>
    );
};

export default BulkImport; 